import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import apiService from "../services/apiService";
import html2canvas from "html2canvas";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Label,
  Legend,
  LabelList,
} from "recharts";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import EmailConfigModal from "../Components/EmailConfigModal";

/* Colors */
const BLUE_TITLE    = "#1E5A96";  // Darker blue for titles
const BLUE_PRIMARY  = "#0066CC";  // Chart blue
const ORANGE_ACCENT = "#FF6B35";  // Chart orange
const WHITE_BG      = "#ffffff";
const BORDER_GRAY   = "#d0d7e0";
const LIGHT_BG      = "#f8fafc";
const TEXT_GRAY     = "#6B7280";  // Gray for subtitles

const WeeklyStatusDashboard = forwardRef(({ 
  selectedApplication = "",
  selectedEnvironment = "",
  dateFrom: propDateFrom,
  setDateFrom: propSetDateFrom,
  dateTo: propDateTo,
  setDateTo: propSetDateTo,
  showEmailSubject: propShowEmailSubject,
  setShowEmailSubject: propSetShowEmailSubject,
  emailConfigOpen: propEmailConfigOpen,
  setEmailConfigOpen: propSetEmailConfigOpen,
}, ref) => {
  const dashboardRef = useRef(null);
  const highlightRef = useRef(null);

  const [editMode, setEditMode] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ open: false, message: '', severity: 'info' });
  const [customEmailSubject, setCustomEmailSubject] = useState("");

  // Use props if provided, otherwise use internal state
  const [internalDateFrom, setInternalDateFrom] = useState("2026-01-26");
  const [internalDateTo, setInternalDateTo] = useState("2026-02-01");
  const [internalShowEmailSubject, setInternalShowEmailSubject] = useState(false);
  const [internalEmailConfigOpen, setInternalEmailConfigOpen] = useState(false);

  const dateFrom = propDateFrom !== undefined ? propDateFrom : internalDateFrom;
  const setDateFrom = propSetDateFrom || setInternalDateFrom;
  const dateTo = propDateTo !== undefined ? propDateTo : internalDateTo;
  const setDateTo = propSetDateTo || setInternalDateTo;
  const showEmailSubject = propShowEmailSubject !== undefined ? propShowEmailSubject : internalShowEmailSubject;
  const setShowEmailSubject = propSetShowEmailSubject || setInternalShowEmailSubject;
  const emailConfigOpen = propEmailConfigOpen !== undefined ? propEmailConfigOpen : internalEmailConfigOpen;
  const setEmailConfigOpen = propSetEmailConfigOpen || setInternalEmailConfigOpen;

  const [highlightsText, setHighlightsText] = useState("");

  const highlightLines = highlightsText
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const [kpiData, setKpiData] = useState({
    totalJobsMonitored: 0,
    jobsSuccessful: 0,
    jobsFailedRecovered: 0,
  });

  const [weeklyJobsByType, setWeeklyJobsByType] = useState([
    { type: "Weekday", count: 0 },
    { type: "Weekend", count: 0 },
  ]);

  const [dailyJobStatus, setDailyJobStatus] = useState([]);
  const [jobsByCriticality, setJobsByCriticality] = useState([]);

  // Helper function to convert date string to Date object
  const toDateObj = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
  };

  // Generate dynamic title based on application selection
  const generateTitle = useCallback(() => {
    // For GARPAC and OMEGA, keep the same title format
    let appName;
    if (selectedApplication === 'GARPAC' || selectedApplication === 'OMEGA' || selectedApplication === 'ALL') {
      appName = 'GARPAC/Omega';
    } else {
      // For other specific applications, use the application name
      appName = selectedApplication;
    }
    
    return `${appName} Support – Weekly Status Dashboard`;
  }, [selectedApplication]);

  const generateDateRange = () => {
    if (!dateFrom || !dateTo) return "";
    
    const fromDate = new Date(dateFrom + 'T00:00:00');
    const toDate = new Date(dateTo + 'T00:00:00');
    
    const fromDateFormatted = fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const toDateFormatted = toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${fromDateFormatted} to ${toDateFormatted}`;
  };

  // Update custom email subject when application or dates change
  useEffect(() => {
    const generateDefaultSubject = () => {
      if (!dateFrom || !dateTo) {
        return "Weekly Report - GARPAC/Omega Support";
      }

      const fromDateFormatted = new Date(dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const toDateFormatted = new Date(dateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      
      const appName = selectedApplication === 'GARPAC' ? 'GARPAC' : 
                     selectedApplication === 'OMEGA' ? 'OMEGA' : 
                     selectedApplication === 'ALL' ? 'GARPAC/Omega' : selectedApplication;
      
      return `Weekly Report - ${appName} Support - ${fromDateFormatted} to ${toDateFormatted}`;
    };

    const defaultSubject = generateDefaultSubject();
    setCustomEmailSubject(defaultSubject);
  }, [selectedApplication, dateFrom, dateTo]);

  /* 🔹 FETCH REPORT HIGHLIGHTS */
  useEffect(() => {
    const fetchReportHighlights = async () => {
      try {
        if (!dateFrom || !dateTo) return;

        const params = new URLSearchParams({
          date_from: dateFrom,
          date_to: dateTo,
        });

        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }

        const data = await apiService.get(`/garpac-weekly-report/get-weekly-highlights?${params}`);
        setHighlightsText(data.highlights || "");
      } catch (error) {
        console.error("Error fetching report highlights:", error);
        setHighlightsText("");
      }
    };

    fetchReportHighlights();
  }, [dateFrom, dateTo, selectedApplication, selectedEnvironment]);

  /* 🔹 SAVE REPORT HIGHLIGHTS */
  const saveHighlights = async () => {
    try {
      if (!dateFrom || !dateTo) return;

      const autoTitle = generateTitle();

      const payload = {
        title: autoTitle,
        highlights: highlightsText,
        date_from: dateFrom,
        date_to: dateTo,
      };

      if (selectedApplication && selectedApplication !== 'ALL') {
        payload.application = selectedApplication;
      }
      if (selectedEnvironment && selectedEnvironment !== 'ALL') {
        payload.env_id = selectedEnvironment;
      }

      await apiService.post("/garpac-weekly-report/save-weekly-highlights", payload);

      console.log("Highlights saved successfully");
      setEditMode(false);
    } catch (error) {
      console.error("Error saving highlights:", error);
    }
  };

  /* CLICK OUTSIDE */
  useEffect(() => {
    const handleClickOutside = async (e) => {
      if (
        editMode &&
        highlightRef.current &&
        !highlightRef.current.contains(e.target)
      ) {
        // Save when clicking outside
        try {
          if (dateFrom && dateTo) {
            const autoTitle = generateTitle();
            
            const payload = {
              title: autoTitle,
              highlights: highlightsText,
              date_from: dateFrom,
              date_to: dateTo,
            };

            if (selectedApplication && selectedApplication !== 'ALL') {
              payload.application = selectedApplication;
            }
            if (selectedEnvironment && selectedEnvironment !== 'ALL') {
              payload.env_id = selectedEnvironment;
            }
            
            await apiService.post("/garpac-weekly-report/save-weekly-highlights", payload);
            console.log("Highlights saved successfully");
          }
        } catch (error) {
          console.error("Error saving highlights:", error);
        }
        setEditMode(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editMode, highlightsText, dateFrom, dateTo, selectedApplication, selectedEnvironment, generateTitle]);

  // FETCH ALL KPIs
  useEffect(() => {
    const fetchAllKPIs = async () => {
      try {
        const params = new URLSearchParams();
        if (dateFrom && dateTo) {
          params.append('date_from', dateFrom);
          params.append('date_to', dateTo);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }
        
        let endpoint = "/garpac-weekly-report/all-weekly-kpis";
        if (params.toString()) {
          endpoint += `?${params}`;
        }
        
        const data = await apiService.get(endpoint);
        setKpiData({
          totalJobsMonitored: data.totalJobsMonitored || 0,
          jobsSuccessful: data.jobsSuccessful || 0,
          jobsFailedRecovered: data.jobsFailedRecovered || 0,
        });
      } catch (error) {
        console.error("Error fetching KPIs:", error);
      }
    };
    fetchAllKPIs();
  }, [dateFrom, dateTo, selectedApplication, selectedEnvironment]);

  // FETCH WEEKLY JOBS BY TYPE
  useEffect(() => {
    const fetchWeeklyJobsByType = async () => {
      try {
        const params = new URLSearchParams();
        if (dateFrom && dateTo) {
          params.append('date_from', dateFrom);
          params.append('date_to', dateTo);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }
        
        let endpoint = "/garpac-weekly-report/weekly-jobs-by-type";
        if (params.toString()) {
          endpoint += `?${params}`;
        }
        
        const data = await apiService.get(endpoint);
        setWeeklyJobsByType(data || [
          { type: "Weekday", count: 0 },
          { type: "Weekend", count: 0 },
        ]);
      } catch (error) {
        console.error("Error fetching jobs by type:", error);
      }
    };
    fetchWeeklyJobsByType();
  }, [dateFrom, dateTo, selectedApplication, selectedEnvironment]);

  // FETCH DAILY JOB STATUS
  useEffect(() => {
    const fetchDailyJobStatus = async () => {
      try {
        const params = new URLSearchParams();
        if (dateFrom && dateTo) {
          params.append('date_from', dateFrom);
          params.append('date_to', dateTo);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }
        
        let endpoint = "/garpac-weekly-report/daily-job-status";
        if (params.toString()) {
          endpoint += `?${params}`;
        }
        
        const data = await apiService.get(endpoint);
        const formatted = data.map(item => ({
          date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          completed: item.completed || 0,
          failedRecovered: item.failedRecovered || 0,
        }));
        setDailyJobStatus(formatted);
      } catch (error) {
        console.error("Error fetching daily status:", error);
        setDailyJobStatus([]);
      }
    };
    fetchDailyJobStatus();
  }, [dateFrom, dateTo, selectedApplication, selectedEnvironment]);

  // FETCH JOBS BY CRITICALITY
  useEffect(() => {
    const fetchJobsByCriticality = async () => {
      try {
        const params = new URLSearchParams();
        if (dateFrom && dateTo) {
          params.append('date_from', dateFrom);
          params.append('date_to', dateTo);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }
        
        let endpoint = "/garpac-weekly-report/jobs-by-criticality";
        if (params.toString()) {
          endpoint += `?${params}`;
        }
        
        const data = await apiService.get(endpoint);
        setJobsByCriticality(data || []);
      } catch (error) {
        console.error("Error fetching criticality:", error);
        setJobsByCriticality([]);
      }
    };
    fetchJobsByCriticality();
  }, [dateFrom, dateTo, selectedApplication, selectedEnvironment]);

  const downloadDashboard = async () => {
    if (!dashboardRef.current) return;
    try {
      const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `weekly-report-${dateFrom}-to-${dateTo}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  /* 🔹 SEND REPORT EMAIL */
  const sendReportEmail = async () => {
    if (!dashboardRef.current) return;
    
    setSendingEmail(true);
    
    try {
      const reportData = {
        highlights: highlightsText,
        kpis: kpiData
      };

      try {
        // First, try to send with image
        const canvas = await html2canvas(dashboardRef.current, {
          scale: 1.5,
          useCORS: true,
          quality: 0.8,
          backgroundColor: '#ffffff'
        });
        
        const imageData = canvas.toDataURL("image/jpeg", 0.8);

        const imageSizeKB = Math.round(imageData.length / 1024);
        console.log(`Image size: ${imageSizeKB}KB`);

        if (imageSizeKB > 5000) {
          throw new Error('Image too large, using text-only email');
        }

        const response = await apiService.post('/email/send-report', {
          reportType: 'weekly',
          reportData,
          imageData,
          customSubject: customEmailSubject
        });

        setEmailStatus({
          open: true,
          message: response.message || 'Weekly report sent successfully with image!',
          severity: 'success'
        });

      } catch (imageError) {
        console.log('Image email failed, trying text-only:', imageError.message);
        
        const response = await apiService.post('/email/send-report-text', {
          reportType: 'weekly',
          reportData,
          customSubject: customEmailSubject
        });

        setEmailStatus({
          open: true,
          message: response.message || 'Weekly report sent successfully (text format)!',
          severity: 'success'
        });
      }

    } catch (error) {
      console.error('Error sending email:', error);
      
      let errorMessage = 'Failed to send email. Please check your email configuration.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        
        if (errorMessage.includes('No email configuration found')) {
          errorMessage += ' Click the settings icon (⚙️) to configure email recipients.';
        } else if (errorMessage.includes('Email configuration is disabled')) {
          errorMessage = 'Email is configured but disabled. Click the settings icon (⚙️) to enable email sending.';
        }
      }
      
      setEmailStatus({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    downloadDashboard,
    sendEmail: sendReportEmail,
  }));

  const CustomCenterLabel = ({ viewBox }) => {
    const { cx, cy } = viewBox || { cx: 0, cy: 0 };
    const total = jobsByCriticality.reduce((sum, item) => sum + (item.count || 0), 0);
    return (
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
        <tspan x={cx} dy="-0.4em" fontSize={16} fontWeight="bold" fill={BLUE_PRIMARY}>
          {total || 0}
        </tspan>
        <tspan x={cx} dy="1.4em" fontSize={11} fill={BLUE_PRIMARY}>
          Total
        </tspan>
      </text>
    );
  };

  // Colors for different criticality levels
  const CRITICALITY_COLORS = {
    1: "#0B5ED7",  // Blue
    2: "#FD7E14",  // Orange
    3: "#198754",  // Green
    4: "#6F42C1",  // Purple
    5: "#DC3545",  // Red
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: LIGHT_BG,
        p: { xs: 1.5, md: 3 },
      }}
    >
      {/* Email Subject Input - Controlled from container */}
      {showEmailSubject && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Email Subject"
            value={customEmailSubject}
            onChange={(e) => setCustomEmailSubject(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: BLUE_TITLE,
                },
                '&:hover fieldset': {
                  borderColor: BLUE_TITLE,
                },
                '&.Mui-focused fieldset': {
                  borderColor: BLUE_TITLE,
                },
              },
              '& .MuiInputLabel-root': {
                color: BLUE_TITLE,
                '&.Mui-focused': {
                  color: BLUE_TITLE,
                },
              },
            }}
          />
        </Box>
      )}

      {/* Main Card */}
      <Paper
        ref={dashboardRef}
        sx={{
          border: `1px solid ${BORDER_GRAY}`,
          borderRadius: 1,
          bgcolor: WHITE_BG,
          p: { xs: 2, md: 3 },
          position: "relative",
        }}
      >
        {/* Header - only title & date */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" color={BLUE_TITLE}>
            {generateTitle()}
          </Typography>
          <Typography variant="subtitle1" color={BLUE_TITLE} sx={{ mt: 0.5 }}>
            {generateDateRange()}
          </Typography>
        </Box>

        {/* Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "160px 1fr 1fr 1fr" },
            gap: 2.5,
            alignItems: "stretch",
          }}
        >
          {/* KPIs */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Paper sx={{ p: 2, textAlign: "center", border: `1px solid ${BORDER_GRAY}` }}>
              <Typography variant="caption" sx={{ color: TEXT_GRAY }}>
                # of Jobs Monitored
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: BLUE_TITLE }}>
                {kpiData.totalJobsMonitored || "0"}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: "center", border: `1px solid ${BORDER_GRAY}` }}>
              <Typography variant="caption" sx={{ color: TEXT_GRAY }}>
                # of Jobs Successful
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: BLUE_TITLE }}>
                {kpiData.jobsSuccessful || "0"}
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: "center", border: `1px solid ${BORDER_GRAY}` }}>
              <Typography variant="caption" sx={{ color: TEXT_GRAY }}>
                # of Jobs Failed & Recovered
              </Typography>
              <Typography variant="h4" fontWeight="bold" sx={{ color: BLUE_TITLE }}>
                {kpiData.jobsFailedRecovered || "0"}
              </Typography>
            </Paper>
          </Box>

          {/* Total Jobs Monitored - full height chart */}
          <Paper sx={{ border: `1px solid ${BORDER_GRAY}`, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${BORDER_GRAY}`, textAlign: "center" }}>
              <Typography variant="subtitle2" sx={{ color: BLUE_TITLE, fontWeight: 600, mb: 0.5 }}>
                Total Jobs Monitored
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: TEXT_GRAY }}>Type</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: BLUE_PRIMARY }} />
                  <Typography variant="caption" sx={{ color: TEXT_GRAY }}>Weekday</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ORANGE_ACCENT }} />
                  <Typography variant="caption" sx={{ color: TEXT_GRAY }}>Weekend</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: 1, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={weeklyJobsByType}
                    cx="50%"
                    cy="50%"
                    innerRadius={0}
                    outerRadius={80}
                    paddingAngle={0}
                    dataKey="count"
                    label={({ type, count, percent, x, y, cx, cy }) => {
                      const radius = 95;
                      const midAngle = Math.atan2(y - cy, x - cx);
                      const labelX = cx + radius * Math.cos(midAngle);
                      const labelY = cy + radius * Math.sin(midAngle);
                      
                      return (
                        <text 
                          x={labelX} 
                          y={labelY} 
                          fill={BLUE_TITLE}
                          textAnchor={labelX > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          fontSize={13}
                          fontWeight="bold"
                        >
                          {count > 0 ? `${(count / 1000).toFixed(2)}K` : '0'}
                        </text>
                      );
                    }}
                    labelLine={false}
                  >
                    <Cell fill={BLUE_PRIMARY} />
                    <Cell fill={ORANGE_ACCENT} />
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Daily Job Status - full height chart */}
          <Paper sx={{ border: `1px solid ${BORDER_GRAY}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${BORDER_GRAY}` }}>
              <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center', color: BLUE_TITLE, fontWeight: 600 }}>
                Total Successful/Failed Jobs
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <Typography variant="caption" sx={{ color: TEXT_GRAY, fontWeight: 500 }}>
                  Job Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: BLUE_PRIMARY }} />
                  <Typography variant="caption" sx={{ color: TEXT_GRAY }}>Completed</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 8, borderRadius: '10px', bgcolor: ORANGE_ACCENT }} />
                  <Typography variant="caption" sx={{ color: TEXT_GRAY }}>Failed and Recovered</Typography>
                </Box>
              </Box>
            </Box>
            {/* Chart Container with flex */}
            <Box sx={{ flex: 1, p: 0.5, pt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={dailyJobStatus} 
                  margin={{ top: 45, right: 10, bottom: 50, left: 45 }}
                  barGap={10}
                  barCategoryGap="15%"
                >
                  <XAxis 
                    dataKey="date" 
                    angle={-45} 
                    textAnchor="end" 
                    height={60} 
                    tick={{ fontSize: 11, fill: TEXT_GRAY }}
                    interval={0}
                    label={{ 
                      value: 'Date Monitored (IST)', 
                      position: 'insideBottom', 
                      offset: -12, 
                      style: { fontSize: 12, fill: BLUE_TITLE, fontWeight: 500 } 
                    }}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fill: TEXT_GRAY }} 
                    width={40}
                    domain={[0, 'dataMax + 50']}
                    label={{ 
                      value: 'Jobs Monitored', 
                      angle: -90, 
                      position: 'insideLeft', 
                      offset: -3, 
                      style: { fontSize: 12, fill: BLUE_TITLE, fontWeight: 500, textAnchor: 'middle' } 
                    }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: `1px solid ${BORDER_GRAY}`,
                      borderRadius: 4 
                    }}
                  />
                  <Bar dataKey="completed" stackId="a" fill={BLUE_PRIMARY} barSize={65} radius={[0, 0, 0, 0]}>
                    <LabelList 
                      dataKey="completed" 
                      position="inside" 
                      style={{ fontSize: 13, fontWeight: 'bold', fill: 'white' }} 
                    />
                  </Bar>
                  <Bar dataKey="failedRecovered" stackId="a" fill={ORANGE_ACCENT} barSize={65} radius={[4, 4, 0, 0]}>
                    <LabelList 
                      content={({ x, y, width, value, index }) => {
                        const item = dailyJobStatus[index];
                        const total = item.completed + item.failedRecovered;
                        return (
                          <text 
                            x={x + width / 2} 
                            y={y - 8} 
                            fill={BLUE_TITLE} 
                            textAnchor="middle" 
                            fontSize={13} 
                            fontWeight="bold"
                          >
                            {total}
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Criticality - full height chart */}
          <Paper sx={{ border: `1px solid ${BORDER_GRAY}`, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${BORDER_GRAY}`, textAlign: "center" }}>
              <Typography variant="subtitle2" sx={{ color: BLUE_TITLE, fontWeight: 600, mb: 0.5 }}>
                Jobs recovered by Criticality
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: TEXT_GRAY }}>Criticality</Typography>
                {jobsByCriticality
                  .filter(item => item.count > 0)
                  .map(item => (
                    <Box key={item.criticality} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: CRITICALITY_COLORS[item.criticality] || BLUE_PRIMARY 
                        }} 
                      />
                      <Typography variant="caption" sx={{ color: TEXT_GRAY }}>{item.criticality}</Typography>
                    </Box>
                  ))}
              </Box>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, minHeight: 0 }}>
              <Box sx={{ width: '100%', height: '100%', maxWidth: 220, maxHeight: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={jobsByCriticality.length ? jobsByCriticality : [{ criticality: 0, skechersCount: 0, bfxCount: 0, count: 0 }]}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      dataKey="count"
                      isAnimationActive={false}
                      label={({ count, x, y, cx, cy, midAngle, innerRadius, outerRadius }) => {
                        if (count === 0) return null;
                        const RADIAN = Math.PI / 180;
                        const radius = outerRadius + 20;
                        const labelX = cx + radius * Math.cos(-midAngle * RADIAN);
                        const labelY = cy + radius * Math.sin(-midAngle * RADIAN);
                        
                        return (
                          <text 
                            x={labelX} 
                            y={labelY} 
                            fill={BLUE_TITLE}
                            textAnchor={labelX > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                            fontSize={13}
                            fontWeight="bold"
                          >
                            {count}
                          </text>
                        );
                      }}
                      labelLine={false}
                    >
                      {jobsByCriticality.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CRITICALITY_COLORS[entry.criticality] || BLUE_PRIMARY} />
                      ))}
                      <Label content={CustomCenterLabel} position="center" />
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `${value} jobs`,
                        `Criticality ${props.payload.criticality}`
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Highlights */}
        <Box
          ref={highlightRef}
          onClick={() => !editMode && setEditMode(true)}
          sx={{
            mt: 3,
            p: 2,
            border: `1px solid ${BORDER_GRAY}`,
            minHeight: 100,
            cursor: editMode ? "default" : "pointer",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Highlights:
          </Typography>

          {!editMode ? (
            <Box sx={{ pl: 1 }}>
              {highlightLines.length === 0 ? (
                <Typography color="text.secondary" fontSize="0.95rem">
                  Click to add/edit highlights...
                </Typography>
              ) : (
                highlightLines.map((line, i) => (
                  <Typography key={i} sx={{ mb: 0.6, fontSize: "0.94rem" }}>
                    • {line}
                  </Typography>
                ))
              )}
            </Box>
          ) : (
            <>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={5}
                maxRows={8}
                value={highlightsText}
                onChange={(e) => setHighlightsText(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ mb: 2, "& .MuiInputBase-input": { fontSize: "0.93rem" } }}
              />
              <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
                <Button size="small" onClick={() => setEditMode(false)}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={saveHighlights}
                  sx={{ bgcolor: BLUE_TITLE }}
                >
                  Save
                </Button>
              </Box>
            </>
          )}
        </Box>

        {/* Footer */}
        
      </Paper>

      {/* Email Configuration Modal */}
      <EmailConfigModal
        reportType="weekly"
        open={emailConfigOpen}
        onClose={() => setEmailConfigOpen(false)}
      />

      {/* Email Status Snackbar */}
      <Snackbar
        open={emailStatus.open}
        autoHideDuration={6000}
        onClose={() => setEmailStatus({ ...emailStatus, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setEmailStatus({ ...emailStatus, open: false })} 
          severity={emailStatus.severity}
          sx={{ width: '100%' }}
        >
          {emailStatus.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default WeeklyStatusDashboard;

